import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { Box, Typography, IconButton, InputBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { NAVIGATION_KEYS, NS } from '../../../i18n/constants/keys';
import { Permission } from '../../../utils/helpers/permissions';
import { RootState } from '../../../utils/redux_store/store';
import {
  BrainIcon,
  BriefcaseBusinessIcon,
  ChartPieIcon,
  ChevronLeftIcon,
  CircleHelpIcon,
  ClipboardCheckIcon,
  CompassIcon,
  DatabaseBackupIcon,
  FilePenLineIcon,
  FileTextIcon,
  HeartHandshakeIcon,
  IdCardIcon,
  LayoutGridIcon,
  LockKeyholeIcon,
  SearchIcon,
  SettingsIcon,
  SlidersHorizontalIcon,
  SparklesIcon,
  UserCheckIcon,
  UserIcon,
  UserRoundCheckIcon,
  UserRoundPlusIcon,
  UsersRoundIcon,
  XIcon,
} from '../../../icons/animated';
import { AppIcon } from './AppIcon';

const MotionBox = motion(Box);

export interface AppsFolderProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AppDefinition {
  id: string;
  name: string;
  description?: string;
  icon: React.ReactNode;
  gradient: string;
  to?: string;
  isFolder?: boolean;
  subItems?: AppDefinition[];
  requiredPermissions?: Permission[];
  matchAnyPermission?: boolean;
  hideIf?: boolean;
}

export const AppsFolder: React.FC<AppsFolderProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(NS.navigation);
  const history = useHistory();
  const location = useLocation();
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const userPermissions = useSelector(
    (state: RootState) => state.permissions.permissions,
  );
  const role = localStorage.getItem('role');

  const hasPermission = (
    requiredPermissions?: Permission[],
    matchAny = false,
  ): boolean => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    if (matchAny) {
      return requiredPermissions.some((p) => userPermissions.includes(p));
    }
    return requiredPermissions.every((p) => userPermissions.includes(p));
  };

  const isLicenseActive = (feature?: {
    startDate: number | string;
    endDate: number | string;
  }): boolean => {
    if (!feature) return false;
    return Number(feature.endDate) * 1000 > Date.now();
  };

  // Close on Escape key and body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeFolderId) {
          setActiveFolderId(null);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      setSearchQuery('');
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, activeFolderId, onClose]);

  // App definitions
  const allApps: AppDefinition[] = useMemo(() => {
    return [
      {
        id: 'dashboard',
        name: t(NAVIGATION_KEYS.sidebar.dashboard) || 'Dashboard',
        description: 'Participant & overview hub',
        icon: <LayoutGridIcon color="#ffffff" size={28} />,
        gradient: 'from-emerald-500 to-teal-700',
        to: '/home',
      },
      {
        id: 'hierTalent',
        name: t(NAVIGATION_KEYS.sidebar.hierTalent) || 'HierTalent',
        description: 'Job openings & candidate pipelines',
        icon: <BriefcaseBusinessIcon color="#ffffff" size={28} />,
        gradient: 'from-blue-600 to-indigo-700',
        to: '/job-openings-list',
        requiredPermissions: [
          Permission.ASSESSMENTS_CREATE,
          Permission.QUESTIONS_VIEW,
        ],
        matchAnyPermission: true,
      },
      {
        id: 'questions_folder',
        name: t(NAVIGATION_KEYS.sidebar.questions) || 'Questions',
        description: 'Question bank, prescreen & CCA',
        icon: <CircleHelpIcon color="#ffffff" size={28} />,
        gradient: 'from-purple-600 via-pink-600 to-rose-600',
        isFolder: true,
        requiredPermissions: [Permission.QUESTIONS_VIEW],
        subItems: [
          {
            id: 'question_bank',
            name: t(NAVIGATION_KEYS.sidebar.questionBank) || 'Question Bank',
            description: 'Central repository of assessment questions',
            icon: <DatabaseBackupIcon color="#ffffff" size={28} />,
            gradient: 'from-purple-500 to-indigo-600',
            to: '/question-bank',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
          },
          {
            id: 'pre_screen',
            name: t(NAVIGATION_KEYS.sidebar.preScreenQuestions) || 'Pre-Screen Questions',
            description: 'Candidate screening questions',
            icon: <ClipboardCheckIcon color="#ffffff" size={28} />,
            gradient: 'from-fuchsia-500 to-pink-600',
            to: '/pre-screen-bank',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
          },
          {
            id: 'content_manager',
            name: t(NAVIGATION_KEYS.sidebar.contentManager) || 'Content Manager',
            description: 'Custom question and job content manager',
            icon: <FilePenLineIcon color="#ffffff" size={28} />,
            gradient: 'from-pink-500 to-rose-600',
            to: '/content-manager',
            requiredPermissions: [Permission.JOB_VIEW],
          },
          {
            id: 'auto_generate',
            name: t(NAVIGATION_KEYS.sidebar.autoGenerateQuestions) || 'Auto Generate Questions',
            description: 'Generate questions automatically from documents',
            icon: <SparklesIcon color="#ffffff" size={28} />,
            gradient: 'from-rose-500 to-orange-500',
            to: '/upload-file',
            requiredPermissions: [Permission.JOB_CREATE],
          },
          {
            id: 'cca_questions',
            name: t(NAVIGATION_KEYS.sidebar.ccaQuestions) || 'CCA Questions',
            description: 'Cognitive & behavioural assessments',
            icon: <BrainIcon color="#ffffff" size={28} />,
            gradient: 'from-violet-600 to-purple-800',
            to: '/cca-questions',
            requiredPermissions: [Permission.QUESTIONS_VIEW],
            hideIf: !isLicenseActive(subscriptions?.hierTalentCca),
          },
        ],
      },
      {
        id: 'reporting',
        name: t(NAVIGATION_KEYS.sidebar.reporting) || 'Reporting',
        description: 'Analytics, funnels and PDF exports',
        icon: <ChartPieIcon color="#ffffff" size={28} />,
        gradient: 'from-amber-500 to-orange-600',
        to: '/reporting',
      },
      {
        id: 'job_postings',
        name: t(NAVIGATION_KEYS.sidebar.jobPostings) || 'Job Postings',
        description: 'Active public job board & listings',
        icon: <FileTextIcon color="#ffffff" size={28} />,
        gradient: 'from-cyan-500 to-blue-600',
        to: '/jobs',
      },
      {
        id: 'candibank',
        name: t(NAVIGATION_KEYS.sidebar.candibankExplorer) || 'Candibank Explorer',
        description: 'Explore curated candidate talent bank',
        icon: <UsersRoundIcon color="#ffffff" size={28} />,
        gradient: 'from-teal-600 to-emerald-700',
        to: '/candibank-explorer',
        hideIf: !subscriptions?.hierTalentCandiBank || role === 'Candidate',
      },
      {
        id: 'settings_folder',
        name: 'System Settings',
        description: 'Access management, organization & frameworks',
        icon: <SettingsIcon color="#ffffff" size={28} />,
        gradient: 'from-slate-700 via-slate-800 to-zinc-900',
        isFolder: true,
        hideIf: role === 'Candidate',
        subItems: [
          {
            id: 'settings_overview',
            name: 'Settings Overview',
            description: 'System settings dashboard and summary',
            icon: <SettingsIcon color="#ffffff" size={28} />,
            gradient: 'from-slate-600 to-zinc-700',
            to: '/system-settings',
          },
          {
            id: 'access_management',
            name: 'Users & Roles',
            description: 'Manage users, roles and permission access',
            icon: <UserRoundPlusIcon color="#ffffff" size={28} />,
            gradient: 'from-blue-600 to-indigo-700',
            to: '/access-management',
            requiredPermissions: [Permission.USERS_VIEW],
          },
          {
            id: 'assessment_framework',
            name: 'Assessment Framework',
            description: 'Configure assessment frameworks & traits',
            icon: <CompassIcon color="#ffffff" size={28} />,
            gradient: 'from-purple-600 to-indigo-700',
            to: '/assessment-framework',
            requiredPermissions: [Permission.ASSESSMENTS_VIEW],
          },
          {
            id: 'organization_settings',
            name: 'Organization Settings',
            description: 'Organization units, branding and preferences',
            icon: <SlidersHorizontalIcon color="#ffffff" size={28} />,
            gradient: 'from-teal-600 to-emerald-700',
            to: '/organization-settings',
            requiredPermissions: [Permission.ORGUNIT_VIEW],
          },
          {
            id: 'employee_management',
            name: 'Employee Management',
            description: 'Manage staff and employee directory',
            icon: <IdCardIcon color="#ffffff" size={28} />,
            gradient: 'from-emerald-600 to-teal-800',
            to: '/employee-management',
          },
          {
            id: 'candidate_management',
            name: 'Candidate Management',
            description: 'Talent candidates and application pipelines',
            icon: <UserCheckIcon color="#ffffff" size={28} />,
            gradient: 'from-cyan-600 to-blue-700',
            to: '/candidate-management',
          },
          {
            id: 'partner_management',
            name: 'Partner Management',
            description: 'External partners and integrations',
            icon: <HeartHandshakeIcon color="#ffffff" size={28} />,
            gradient: 'from-amber-600 to-orange-700',
            to: '/partner-management',
          },
        ],
      },
      {
        id: 'profile_folder',
        name: 'Profile & Account',
        description: 'User profile, password & security',
        icon: <UserRoundCheckIcon color="#ffffff" size={28} />,
        gradient: 'from-teal-600 via-emerald-700 to-green-800',
        isFolder: true,
        subItems: [
          {
            id: 'user_profile',
            name: 'My Profile',
            description: 'Personal details and account preferences',
            icon: <UserIcon color="#ffffff" size={28} />,
            gradient: 'from-emerald-600 to-teal-700',
            to: '/user-profile',
          },
          {
            id: 'change_password',
            name: 'Change Password',
            description: 'Update password and security credentials',
            icon: <LockKeyholeIcon color="#ffffff" size={28} />,
            gradient: 'from-slate-700 to-slate-900',
            to: '/change-password',
          },
        ],
      },
    ];
  }, [t, subscriptions, role, userPermissions]);

  // Filter accessible apps
  const accessibleApps = useMemo(() => {
    return allApps
      .filter((app) => !app.hideIf && hasPermission(app.requiredPermissions, app.matchAnyPermission))
      .map((app) => {
        if (!app.subItems) return app;
        return {
          ...app,
          subItems: app.subItems.filter(
            (sub) => !sub.hideIf && hasPermission(sub.requiredPermissions, sub.matchAnyPermission),
          ),
        };
      });
  }, [allApps, userPermissions]);

  // Auto-detect and open into contextual folder if on a sub-route (e.g. Questions)
  useEffect(() => {
    if (isOpen) {
      const matchedFolder = accessibleApps.find(
        (app) =>
          app.isFolder &&
          app.subItems?.some(
            (sub) =>
              sub.to === location.pathname ||
              (sub.to && sub.to !== '/' && location.pathname.startsWith(sub.to)),
          ),
      );
      if (matchedFolder) {
        setActiveFolderId(matchedFolder.id);
      } else {
        setActiveFolderId(null);
      }
    }
  }, [isOpen, location.pathname, accessibleApps]);

  // Active folder definition
  const activeFolder = useMemo(() => {
    if (!activeFolderId) return null;
    return accessibleApps.find((a) => a.id === activeFolderId && a.isFolder);
  }, [activeFolderId, accessibleApps]);

  // Filtered list based on search
  const displayedApps = useMemo(() => {
    if (activeFolder) {
      return activeFolder.subItems || [];
    }
    if (!searchQuery.trim()) {
      return accessibleApps;
    }
    const q = searchQuery.toLowerCase();
    const matches: AppDefinition[] = [];

    accessibleApps.forEach((app) => {
      if (app.name.toLowerCase().includes(q) || (app.description && app.description.toLowerCase().includes(q))) {
        matches.push(app);
      }
      if (app.subItems) {
        app.subItems.forEach((sub) => {
          if (sub.name.toLowerCase().includes(q) || (sub.description && sub.description.toLowerCase().includes(q))) {
            if (!matches.some((m) => m.id === sub.id)) {
              matches.push(sub);
            }
          }
        });
      }
    });

    return matches;
  }, [accessibleApps, activeFolder, searchQuery]);

  const handleAppClick = (app: AppDefinition) => {
    if (app.isFolder) {
      setActiveFolderId(app.id);
    } else if (app.to) {
      history.push(app.to);
      onClose();
    }
  };

  return (
    <MotionConfig transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 1 }}>
      <AnimatePresence>
        {isOpen && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 100000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 3 },
              userSelect: 'none',
            }}
          >
            {/* Reduced Light Backdrop */}
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: 'rgba(15, 23, 42, 0.2)',
                backdropFilter: 'blur(2px)',
              }}
            />

            {/* iOS App Folder Modal Container */}
            <MotionBox
              initial={{ opacity: 0, scale: 0.92, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{
                opacity: 0,
                scale: 0.94,
                y: 8,
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              transition={{
                type: 'spring',
                stiffness: 380,
                damping: 28,
                mass: 0.8,
              }}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 672,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                borderRadius: '24px',
                p: { xs: 3, sm: 4 },
                color: '#1e293b',
                boxShadow:
                  '0 25px 60px -15px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                zIndex: 10,
              }}
            >
              {/* Header / Search / Title */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {activeFolder ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setActiveFolderId(null)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '12px',
                          bgcolor: '#f1f5f9',
                          color: '#334155',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border: '1px solid rgba(226, 232, 240, 0.6)',
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: '#e2e8f0' },
                          '&:active': { transform: 'scale(0.95)' },
                        }}
                      >
                        <ChevronLeftIcon size={16} />
                        Back
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '12px',
                          bgcolor: '#ecfdf5',
                          color: '#059669',
                          border: '1px solid #a7f3d0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LayoutGridIcon size={16} />
                      </Box>
                    )}

                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0f172a', letterSpacing: '-0.025em' }}>
                        {activeFolder ? activeFolder.name : 'HiAcuity Applications'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {activeFolder
                          ? activeFolder.description || 'Application sub-items'
                          : 'Quick launch apps and workflows'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Close Button */}
                  <IconButton
                    type="button"
                    onClick={onClose}
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: '#f1f5f9',
                      color: '#64748b',
                      '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' },
                    }}
                    aria-label="Close apps dock"
                  >
                    <XIcon size={16} />
                  </IconButton>
                </Box>

                {/* Search Bar */}
                {!activeFolder && (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        pl: 1.75,
                        display: 'flex',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        color: '#94a3b8',
                        zIndex: 1,
                      }}
                    >
                      <SearchIcon size={16} />
                    </Box>
                    <InputBase
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search applications..."
                      fullWidth
                      sx={{
                        pl: 5,
                        pr: 4,
                        py: 0.75,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        color: '#1e293b',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.6)' },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                          borderColor: '#10b981',
                          boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
                        },
                      }}
                    />
                    {searchQuery && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          right: 8,
                          transform: 'translateY(-50%)',
                          color: '#94a3b8',
                          '&:hover': { color: '#475569' },
                        }}
                        aria-label="Clear search"
                      >
                        <XIcon size={16} />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>

              {/* Apps Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: { xs: 2, sm: 3 },
                  maxHeight: '50vh',
                  overflowY: 'auto',
                  p: 0.5,
                }}
              >
                {displayedApps.length > 0 ? (
                  displayedApps.map((app) => {
                    const isActive =
                      app.to === location.pathname ||
                      (app.subItems && app.subItems.some((s) => s.to === location.pathname));

                    return (
                      <AppIcon
                        key={app.id}
                        id={app.id}
                        name={app.name}
                        description={app.description}
                        icon={app.icon}
                        gradient={app.gradient}
                        isActive={isActive}
                        isFolder={app.isFolder}
                        itemCount={app.subItems?.length}
                        onClick={() => handleAppClick(app)}
                      />
                    );
                  })
                ) : (
                  <Box sx={{ gridColumn: '1 / -1', py: 6, textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                    No applications found matching &quot;{searchQuery}&quot;
                  </Box>
                )}
              </Box>

              {/* Bottom Keyboard Hint */}
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  fontSize: '11px',
                  color: '#64748b',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="kbd" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '10px', color: '#334155', fontWeight: 600 }}>
                      Ctrl
                    </Box>
                    <span>+</span>
                    <Box component="kbd" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '10px', color: '#334155', fontWeight: 600 }}>
                      Space
                    </Box>
                    <Box component="span" sx={{ color: '#94a3b8', mx: 0.5 }}>or</Box>
                    <Box component="kbd" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '10px', color: '#334155', fontWeight: 600 }}>
                      ⌘
                    </Box>
                    <span>+</span>
                    <Box component="kbd" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '10px', color: '#334155', fontWeight: 600 }}>
                      Space
                    </Box>
                    <Box component="span" sx={{ color: '#94a3b8', ml: 0.5 }}>to toggle</Box>
                  </Box>
                  <Box component="span" sx={{ color: '#cbd5e1' }}>•</Box>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="kbd" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '10px', color: '#475569' }}>
                      ESC
                    </Box>
                    to close
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  HiAcuity Workspace
                </Typography>
              </Box>
            </MotionBox>
          </Box>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};
